/**
 * cart-upsell-carousel.js
 * ------------------------------------------------------------------
 * Web component <cart-upsell-carousel> : affiche un seul produit
 * suggéré à la fois, avec flèches précédent/suivant et défilement
 * automatique. Distinct de cart-upsell.js (liste complète).
 * ------------------------------------------------------------------
 */

class CartUpsellCarousel extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.excludeIds = (this.dataset.exclude || '')
      .split(',')
      .filter(Boolean)
      .map((id) => id.trim());
    this.index = 0;
    this.products = [];
    this.autoplayDelay = 4500;

    if (!this.productId) {
      this.remove();
      return;
    }

    this.load();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  async load() {
    try {
      let products = await this.fetchRecommendations('complementary');
      if (products.length < 3) {
        const related = await this.fetchRecommendations('related');
        const existingIds = new Set(products.map((p) => p.id));
        related.forEach((p) => {
          if (!existingIds.has(p.id)) products.push(p);
        });
      }

      products = products.filter((p) => !this.excludeIds.includes(String(p.id))).slice(0, 6);

      if (products.length === 0) {
        this.hidden = true;
        return;
      }

      this.products = products;
      this.render();
      if (this.products.length > 1) this.startAutoplay();
    } catch (error) {
      this.hidden = true;
      console.error('cart-upsell-carousel: échec du chargement', error);
    }
  }

  async fetchRecommendations(intent) {
    const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${this.productId}&limit=10&intent=${intent}`;
    try {
      const response = await fetch(url);
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      return [];
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => this.go(1), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
  }

  go(direction) {
    this.index = (this.index + direction + this.products.length) % this.products.length;
    this.render();
  }

  render() {
    const product = this.products[this.index];
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const image = product.featured_image || product.images[0];
    const disabled = !variant || !variant.available;
    const showArrows = this.products.length > 1;

    this.innerHTML = `
      <p class="cart-upsell-carousel__heading">Complétez avec : <strong>${this.escapeHtml(product.title)}</strong></p>
      <div class="cart-upsell-carousel__row">
        ${
          showArrows
            ? `<button type="button" class="cart-upsell-carousel__arrow" data-prev aria-label="Suggestion précédente">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 3 5 8l5 5"/></svg>
              </button>`
            : ''
        }
        <div class="cart-upsell-carousel__media">
          ${
            image
              ? `<img src="${this.resizeImage(image, 200)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="120" height="120">`
              : ''
          }
        </div>
        <button type="button" class="cart-upsell-carousel__add" ${disabled ? 'disabled' : ''} data-add data-variant-id="${variant ? variant.id : ''}">
          <span class="cart-upsell-carousel__add-label">Ajouter</span>
          <span class="cart-upsell-carousel__add-price">/ ${this.formatMoney(variant ? variant.price : product.price)}</span>
          <span class="cart-upsell-carousel__spinner" aria-hidden="true"></span>
        </button>
        ${
          showArrows
            ? `<button type="button" class="cart-upsell-carousel__arrow" data-next aria-label="Suggestion suivante">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3l5 5-5 5"/></svg>
              </button>`
            : ''
        }
      </div>
    `;

    const prevBtn = this.querySelector('[data-prev]');
    const nextBtn = this.querySelector('[data-next]');
    const addBtn = this.querySelector('[data-add]');

    if (prevBtn) prevBtn.addEventListener('click', () => { this.go(-1); this.startAutoplay(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { this.go(1); this.startAutoplay(); });
    if (addBtn && !addBtn.disabled) addBtn.addEventListener('click', () => this.quickAdd(addBtn));
  }

  async quickAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    button.disabled = true;
    this.stopAutoplay();

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!response.ok) throw new Error('add.js a échoué');

      await this.refreshCart();
    } catch (error) {
      console.error('cart-upsell-carousel: échec de l’ajout', error);
      button.classList.remove('is-loading');
      button.disabled = false;
      this.startAutoplay();
    }
  }

  async refreshCart() {
    try {
      const response = await fetch(window.location.href, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return;
      const html = await response.text();
      const freshDoc = new DOMParser().parseFromString(html, 'text/html');

      this.swapNode('#CartDrawer', freshDoc);
      this.swapNode('cart-items', freshDoc);
      this.swapNode('#cart-icon-bubble', freshDoc);

      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'cart-upsell-carousel' } })
      );
    } catch (error) {
      console.error('cart-upsell-carousel: échec du rafraîchissement', error);
    }
  }

  swapNode(selector, freshDoc) {
    const current = document.querySelector(selector);
    const fresh = freshDoc.querySelector(selector);
    if (!current || !fresh) return false;

    const preservedClasses = [...current.classList];
    const hadOpenAttr = current.hasAttribute('open');

    current.replaceWith(fresh);
    fresh.classList.add(...preservedClasses);
    if (hadOpenAttr) fresh.setAttribute('open', '');

    return true;
  }

  resizeImage(url, size) {
    if (!url) return '';
    return url.includes('?') ? `${url}&width=${size}` : `${url}?width=${size}`;
  }

  formatMoney(cents) {
    const format = (this.dataset.moneyFormat || '{{amount}} €').replace(/<[^>]*>/g, '');
    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholderMatch) return format;

    const withDelimiters = (number, precision, thousands, decimal) => {
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const decimals = parts[1] ? decimal + parts[1] : '';
      return whole + decimals;
    };

    let value;
    switch (placeholderMatch[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount':
      default:
        value = withDelimiters(cents, 2, ',', '.');
        break;
    }

    return format.replace(placeholderMatch[0], value);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('cart-upsell-carousel', CartUpsellCarousel);
